import { Image, ScorePill, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { Dialog, ScoreRadialProgress } from '@vizit/molecules';

export interface BenchmarkImagePreview {
  /**
   * The file name, used as the dialog's accessible name and the image alt text
   */
  name: string;
  /**
   * Large rendition of the image
   */
  imageUrl: string | null;
  /**
   * Full-size image to show when `imageUrl` fails to load
   */
  fallbackImageUrl?: string | null;
  /**
   * The image's Vizit score; `null` while it is not scored against a category
   */
  score: number | null;
  /**
   * "Hero" for hero benchmarks, otherwise the image's carousel classification
   */
  assetClass: string;
}

export interface BenchmarkImagePreviewDialogProps {
  /**
   * The image to preview; the dialog is closed while `null`
   */
  image: BenchmarkImagePreview | null;
  /**
   * Called with `false` when the dialog closes from ×, a click outside or Esc
   */
  onOpenChange: (isOpen: boolean) => void;
}

/**
 * A lightweight preview of a benchmark image, not the asset details page: the score ring with its "Vizit score"
 * label and tier pill, the asset class, then the image. Closes with ×, a click outside or Esc.
 */
export function BenchmarkImagePreviewDialog({ image, onOpenChange }: BenchmarkImagePreviewDialogProps) {
  return (
    <Dialog isOpen={!!image} onOpenChange={onOpenChange}>
      <Dialog.Overlay>
        <Dialog.Content aria-label={image ? `Preview of ${image.name}` : 'Image preview'} dialogClassName="gap-8 p-12">
          {image && (
            <>
              <div className="flex items-start gap-12 pr-16">
                <div className="flex items-center gap-4">
                  {image.score !== null ? (
                    <>
                      <ScoreRadialProgress score={image.score} size={40} />
                      <div className="flex flex-col items-start gap-2">
                        <Text size={TextSize.Xs} color={TextColor.Tertiary}>
                          Vizit score
                        </Text>
                        <ScorePill score={image.score} />
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-start gap-2">
                      <Text size={TextSize.Xs} color={TextColor.Tertiary}>
                        Vizit score
                      </Text>
                      <Text size={TextSize.Sm} color={TextColor.Quaternary}>
                        Not scored yet
                      </Text>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Text size={TextSize.Xs} color={TextColor.Tertiary}>
                    Asset class
                  </Text>
                  <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Primary}>
                    {image.assetClass}
                  </Text>
                </div>
              </div>
              <Image
                src={image.imageUrl}
                fallbackSrc={image.fallbackImageUrl}
                name={image.name}
                shape="unset"
                className="aspect-square w-full"
              />
            </>
          )}
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}
